import { useState } from "react";
import { Download, FileText, ExternalLink } from "lucide-react";
import { AGREEMENT_TEMPLATE_URL, AGREEMENT_TITLE, AGREEMENT_LAST_UPDATED } from "@shared/agreement";

/**
 * Presents the approved Master Agent Participation Agreement for reading.
 *
 * Shows the real PDF rather than a re-typeset copy, so what the associate reads
 * is exactly what they sign and exactly what the client approved.
 *
 * Served from our own origin (/api/agreement/master.pdf), so unlike the
 * DocuSeal embed there is no X-Frame-Options problem here.
 *
 * Embedded PDF viewing is not universal, particularly on older mobile
 * browsers, so Open and Download are always offered alongside the frame
 * rather than only as a fallback. Nobody should be asked to sign a document
 * they could not open.
 */
export default function AgreementViewer({ theme = "dark" }: { theme?: "dark" | "light" }) {
  const [frameFailed, setFrameFailed] = useState(false);
  const dark = theme === "dark";

  const shell = dark ? "bg-white/5 border-white/10" : "bg-white border-gray-200";
  const title = dark ? "text-white" : "text-gray-900";
  const muted = dark ? "text-white/50" : "text-gray-500";
  const link = dark
    ? "text-[#84ab4a] hover:text-[#9cc55f]"
    : "text-[#1A6FD4] hover:text-blue-700";

  return (
    <div className={`border rounded-xl overflow-hidden ${shell}`}>
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-white/10">
        <div className="min-w-0">
          <div className={`text-sm font-semibold flex items-center gap-2 ${title}`}>
            <FileText className="w-4 h-4 flex-shrink-0 text-[#84ab4a]" />
            <span className="truncate">{AGREEMENT_TITLE}</span>
          </div>
          <div className={`text-[11px] mt-0.5 ${muted}`}>Last updated {AGREEMENT_LAST_UPDATED}</div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <a
            href={AGREEMENT_TEMPLATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-xs flex items-center gap-1 ${link}`}
          >
            <ExternalLink className="w-3 h-3" /> Open
          </a>
          <a
            href={`${AGREEMENT_TEMPLATE_URL}?download=1`}
            className={`text-xs flex items-center gap-1 ${link}`}
          >
            <Download className="w-3 h-3" /> Download
          </a>
        </div>
      </div>

      {frameFailed ? (
        <div className={`px-4 py-8 text-center text-sm ${muted}`}>
          <p>This browser cannot display the agreement inline.</p>
          <p className="mt-1">
            Please use{" "}
            <a href={AGREEMENT_TEMPLATE_URL} target="_blank" rel="noopener noreferrer" className={link}>
              Open
            </a>{" "}
            or{" "}
            <a href={`${AGREEMENT_TEMPLATE_URL}?download=1`} className={link}>
              Download
            </a>{" "}
            above to read it before signing.
          </p>
        </div>
      ) : (
        <object
          data={AGREEMENT_TEMPLATE_URL}
          type="application/pdf"
          className="w-full block bg-white"
          style={{ height: 420 }}
          aria-label={AGREEMENT_TITLE}
          onError={() => setFrameFailed(true)}
        >
          {/* Rendered by browsers that cannot display the object at all. */}
          <div className={`px-4 py-8 text-center text-sm ${muted}`}>
            <p>This browser cannot display the agreement inline.</p>
            <p className="mt-1">
              Please use{" "}
              <a href={AGREEMENT_TEMPLATE_URL} target="_blank" rel="noopener noreferrer" className={link}>
                Open
              </a>{" "}
              or{" "}
              <a href={`${AGREEMENT_TEMPLATE_URL}?download=1`} className={link}>
                Download
              </a>{" "}
              above to read it before signing.
            </p>
          </div>
        </object>
      )}
    </div>
  );
}
